import clsx from "clsx"
import { useMemo, useState } from "react"

import { faIcon } from "@utils/faIcon"

import Skeleton from "@particles/Skeleton/Skeleton"
import SkeletonTypography from "@particles/SkeletonTypography/SkeletonTypography"

import { LinkButton } from "@atoms"

import { UserAvatarStack } from "@molecules"
import styles from "@molecules/ValueDisplay/ValueDisplay.module.scss"
import type { ValueDisplayVerticalAvatarGroupProps } from "@molecules/ValueDisplay/ValueDisplay.types"

// The design shows up to three avatars before the group truncates.
export const DEFAULT_MAX_AVATARS = 3
// Number of placeholder rows shown while the avatar group is loading.
const LOADING_ROW_COUNT = 3

export default function VerticalAvatarValueDisplay(props: ValueDisplayVerticalAvatarGroupProps) {
    const { className, label, showLabel = true, users, maxAvatars = DEFAULT_MAX_AVATARS, isLoading = false } = props

    const [isExpanded, setIsExpanded] = useState(false)

    const hasUsers = users.length > 0
    const placeholder = `No ${label}`

    // Empty state renders a single "unknown" avatar with the placeholder copy.
    const placeholderUser: AvatarUser = {
        isUnknown: true,
        full_name: placeholder,
        short_name: placeholder,
        avatar: "",
        gravatar: "",
    }

    const limit = isExpanded ? users.length : maxAvatars

    const chevronIcon = useMemo(
        () =>
            isExpanded
                ? faIcon({ name: "angle-up", style: "regular" })
                : faIcon({ name: "angle-down", style: "regular" }),
        [isExpanded],
    )

    const isTruncated = users.length > maxAvatars

    return (
        <div className={clsx(styles.base, styles.vertical, className)}>
            {showLabel && <div className={styles.label}>{label}</div>}

            {isLoading ? (
                <div className={styles.verticalAvatarLoadingSkeleton}>
                    {Array.from({ length: LOADING_ROW_COUNT }).map((_unused, index) => (
                        <div key={index} className={styles.avatarSkeletonRow}>
                            <div className={styles.avatarSkeletonCircle}>
                                <Skeleton circle={true} height={24} width={24} />
                            </div>
                            <SkeletonTypography variant="captionMD" className={styles.avatarSkeletonName} />
                        </div>
                    ))}
                </div>
            ) : (
                <div className={styles.avatarValue}>
                    {hasUsers ? (
                        <>
                            <UserAvatarStack users={users} limit={limit} avatarSize="sm" variation="stack" />
                            {(isTruncated || isExpanded) && (
                                <LinkButton
                                    colorScheme="gray"
                                    rightIcon={chevronIcon}
                                    onClick={() => setIsExpanded((prev) => !prev)}
                                    noDebounce={true}
                                >
                                    {isExpanded ? "Show less" : "Show more"}
                                </LinkButton>
                            )}
                        </>
                    ) : (
                        <UserAvatarStack users={[placeholderUser]} limit={1} avatarSize="sm" variation="stack" />
                    )}
                </div>
            )}
        </div>
    )
}
